/**
 * Shared UI role suffixes used across the component library for data-ui-id attributes.
 *
 * These are common roles that may appear on multiple component types.
 * Components should import from here for any shared role, and define their
 * own component-specific roles locally when needed.
 *
 * Usage:
 *   import { UI_ROLES } from "@/constants/uiRoles";
 *
 *   data-ui-id={`${uiId}-${UI_ROLES.TRIGGER}`}
 */
export const UI_ROLES = {
  TRIGGER: "trigger",
  SEARCH: "search",
  RESET: "reset",
  APPLY: "apply",
  OPTION: "option",
  SELECT_ALL: "select-all",
} as const;

export type UiRole = (typeof UI_ROLES)[keyof typeof UI_ROLES];

/**
 * Role suffixes for the SideNav component.
 *
 * SideNav is a singleton on every page, so identifiers are fixed
 * (no consumer-provided uiId prefix required).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="side-nav"]')
 *   page.locator('[data-ui-id="side-nav-item-equity"]')
 *   page.locator('[data-ui-id="side-nav-pin"]')
 *   page.locator('[data-ui-id="side-nav-admin"]')
 *   page.locator('[data-ui-id="side-nav-submenu"]')
 */
/**
 * Role suffixes for the top Header bar.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="header-title"]')
 */
export const HEADER_UI_ROLES = {
  /** Centered page title — the module label resolved from the metric catalog */
  TITLE: "header-title",
} as const;

export type HeaderUiRole = (typeof HEADER_UI_ROLES)[keyof typeof HEADER_UI_ROLES];

export const SIDE_NAV_UI_ROLES = {
  /** Outermost sidebar wrapper */
  CONTAINER: "side-nav",
  /** Individual nav icon button – append `-{route-id}` */
  ITEM: "side-nav-item",
  /** Pin / unpin toggle button */
  PIN: "side-nav-pin",
  /** Admin settings icon */
  ADMIN: "side-nav-admin",
  /** Expanded sub-menu panel */
  SUBMENU: "side-nav-submenu",
} as const;

export type SideNavUiRole = (typeof SIDE_NAV_UI_ROLES)[keyof typeof SIDE_NAV_UI_ROLES];

/**
 * Role suffixes for the SideNavSubRoute component (expanded sub-menu content).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="side-nav-sub-core-accordion"]')
 *   page.locator('[data-ui-id="side-nav-sub-item-equity-brand"]')
 *   page.locator('[data-ui-id="side-nav-sub-recent-views-accordion"]')
 *   page.locator('[data-ui-id="side-nav-sub-recent-view-v1"]')
 *   page.locator('[data-ui-id="side-nav-sub-signout"]')
 */
export const SIDE_NAV_SUB_ROUTE_UI_ROLES = {
  /** "Core" accordion toggle header */
  CORE_ACCORDION: "side-nav-sub-core-accordion",
  /** Individual child route item – append `-{route-id}` */
  ITEM: "side-nav-sub-item",
  /** "Recent Views" accordion toggle header */
  RECENT_VIEWS_ACCORDION: "side-nav-sub-recent-views-accordion",
  /** Individual recent-view row – append `-{viewId}` */
  RECENT_VIEW: "side-nav-sub-recent-view",
  /** Sign-out button */
  SIGNOUT: "side-nav-sub-signout",
  /** Sub-menu panel heading — the module label resolved from the metric catalog */
  TITLE: "side-nav-sub-title",
} as const;

export type SideNavSubRouteUiRole =
  (typeof SIDE_NAV_SUB_ROUTE_UI_ROLES)[keyof typeof SIDE_NAV_SUB_ROUTE_UI_ROLES];

/**
 * Shared role suffixes for modals. Appended to the modal's own id.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="view-modal-close"]')
 *   page.locator('[data-ui-id="delete-report-modal-confirm"]')
 */
export const MODAL_UI_ROLES = {
  /** Header cross button (rendered by ModalHeader) */
  CLOSE: "close",
  CANCEL: "cancel",
  SAVE: "save",
  CONFIRM: "confirm",
} as const;

/**
 * Role suffixes for the SearchInput component ({uiId}-{role}).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="reports-search-input"]').fill("Q3")
 *   page.locator('[data-ui-id="reports-search-clear"]')
 */
export const SEARCH_INPUT_UI_ROLES = {
  INPUT: "input",
  CLEAR: "clear",
} as const;

/**
 * Identifiers for the dashboard "Save View" menu (SaveView).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="save-view-trigger"]').click()
 *   page.locator('[data-ui-id="save-view-menu-save"]').click()
 *   page.locator('[data-ui-id="save-view-menu-save-as-new"]')
 *   page.locator('[data-ui-id="save-view-menu-copy-url"]')
 */
export const SAVE_VIEW_UI_ROLES = {
  TRIGGER: "save-view-trigger",
  MENU: "save-view-menu",
  /** "Save View" — creates a new view, or edits the one in the URL */
  SAVE: "save-view-menu-save",
  /** "Save as New View" — only rendered when a view is open */
  SAVE_AS_NEW: "save-view-menu-save-as-new",
  COPY_URL: "save-view-menu-copy-url",
} as const;

/**
 * Identifiers for the Create / Edit / Clone View modal (CreateEditViewModal).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="view-modal-name-input"]').fill("My view")
 *   page.locator('[data-ui-id="view-modal-report-select"] input').fill("Q3 report")
 *   page.locator('[data-ui-id="view-modal-default-checkbox"]').check()
 *   page.locator('[data-ui-id="view-modal-save"]').click()
 */
export const VIEW_MODAL_UI_ROLES = {
  CONTAINER: "view-modal",
  /** Title text — "Create View" / "Edit View" / "Clone View" */
  TITLE: "view-modal-title",
  NAME_INPUT: "view-modal-name-input",
  VIOLATIONS: "view-modal-violations",
  /** Wrapper around the react-select creatable report picker */
  REPORT_SELECT: "view-modal-report-select",
  DEFAULT_CHECKBOX: "view-modal-default-checkbox",
  DYNAMIC_DATE_CHECKBOX: "view-modal-dynamic-date-checkbox",
  SAVE_AND_COPY_LINK: "view-modal-save-and-copy-link",
  SAVE: "view-modal-save",
} as const;

/**
 * Identifiers for the Reports list page (/reports).
 *
 * Report cards are dynamic: append `-{reportId}` to CARD, then a
 * REPORT_CARD_UI_ROLES suffix for anything inside the card.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="reports-build-report"]').click()
 *   page.locator('[data-ui-id="reports-list"] > [data-ui-id^="reports-card-"]') // every card
 *   page.locator(`[data-ui-id="reports-card-${id}-open"]`).click()
 *   page.locator(`[data-ui-id="reports-card-${id}-menu-trigger"]`).click()
 *   page.locator(`[data-ui-id="reports-card-${id}-menu-delete"]`).click()
 */
export const REPORTS_UI_ROLES = {
  PAGE: "reports-page",
  TABS: "reports-tabs",
  SEARCH: "reports-search",
  BUILD_REPORT: "reports-build-report",
  EMPTY: "reports-empty",
  LIST: "reports-list",
  /** Individual report card – append `-{reportId}` */
  CARD: "reports-card",
} as const;

/** Suffixes inside a report card: `reports-card-{reportId}-{role}` */
export const REPORT_CARD_UI_ROLES = {
  NAME: "name",
  DESCRIPTION: "description",
  /** "View Report" link */
  OPEN: "open",
  EXPORT_TRIGGER: "export-trigger",
  EXPORT_PPT: "export-ppt",
  EXPORT_EXCEL: "export-excel",
  MENU_TRIGGER: "menu-trigger",
  MENU_EDIT: "menu-edit",
  MENU_EDIT_DATE_PERIOD: "menu-edit-date-period",
  MENU_CLONE: "menu-clone",
  MENU_EXPORT: "menu-export",
  MENU_EXPORT_PPT: "menu-export-ppt",
  MENU_EXPORT_EXCEL: "menu-export-excel",
  MENU_DELETE: "menu-delete",
} as const;

/**
 * Identifiers for the Build / Edit / Clone Report modal
 * (BuildAndUpdateReportModal). View rows are dynamic: append `-{viewId}` to VIEW.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="report-modal-name-input"]').fill("Q3")
 *   page.locator('[data-ui-id="report-modal-views-search-input"]').fill("equity")
 *   page.locator(`[data-ui-id="report-modal-view-${viewId}-checkbox"]`).check()
 *   page.locator('[data-ui-id="report-modal-save"]').click()
 */
export const REPORT_MODAL_UI_ROLES = {
  CONTAINER: "report-modal",
  TITLE: "report-modal-title",
  NAME_INPUT: "report-modal-name-input",
  DESCRIPTION_INPUT: "report-modal-description-input",
  VIEWS_SEARCH: "report-modal-views-search",
  VIEWS_LIST: "report-modal-views-list",
  /** Selectable view row – append `-{viewId}`; checkbox is `-{viewId}-checkbox` */
  VIEW: "report-modal-view",
} as const;

/**
 * Identifiers for the two "Edit Date Period" modals.
 *
 * - REPORT_DATE_PERIOD_MODAL: opened from a report card on /reports
 * - VIEWS_DATE_PERIOD_MODAL:  opened from the bulk-action bar on /reports/:id
 *
 * Each date section is `{modal}-{section}-period-type` / `{modal}-{section}-date-range`,
 * where section is one of DATE_PERIOD_SECTIONS.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="report-date-period-modal-find-period-type"]')
 *   page.locator(`[data-ui-id="report-date-period-modal-matched-view-${viewId}"]`)
 *   page.locator('[data-ui-id="views-date-period-modal-primary-date-range"]')
 *   page.locator('[data-ui-id="views-date-period-modal-save"]').click()
 */
export const DATE_PERIOD_MODAL_UI_ROLES = {
  REPORT_DATE_PERIOD_MODAL: "report-date-period-modal",
  VIEWS_DATE_PERIOD_MODAL: "views-date-period-modal",
  PERIOD_TYPE: "period-type",
  DATE_RANGE: "date-range",
  MATCHED_VIEWS_SEARCH: "matched-views-search",
  MATCHED_VIEWS_LIST: "matched-views-list",
  /** Matched view row – append `-{viewId}` */
  MATCHED_VIEW: "matched-view",
  DYNAMIC_DATE_CHECKBOX: "dynamic-date-checkbox",
} as const;

/** Maps the modals' internal period-type keys to their data-ui-id section token. */
export const DATE_PERIOD_SECTIONS: Record<string, string> = {
  searchViewsPeriodType: "find",
  primaryPeriodType: "primary",
  secondaryPeriodType: "secondary",
};

/**
 * Identifiers for the delete confirmation modals.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="delete-report-modal-confirm"]').click()
 *   page.locator('[data-ui-id="delete-views-modal-count"]')
 */
export const DELETE_MODAL_UI_ROLES = {
  DELETE_REPORT_MODAL: "delete-report-modal",
  /** Legacy-only "Delete views within report" checkbox */
  DELETE_VIEWS_CHECKBOX: "delete-report-modal-delete-views-checkbox",
  DELETE_VIEWS_MODAL: "delete-views-modal",
  /** "N views selected" text */
  VIEWS_COUNT: "delete-views-modal-count",
} as const;

/**
 * Identifiers for the single Report page (/reports/:reportId) — the list of
 * views inside a report.
 *
 * View cards are dynamic: append `-{viewId}` to VIEW_CARD, then a
 * VIEW_CARD_UI_ROLES suffix for anything inside the card.
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="report-breadcrumb-reports"]').click()
 *   page.locator('[data-ui-id="report-views-list"] [data-ui-id^="report-view-card-"]') // every card
 *   page.locator(`[data-ui-id="report-view-card-${id}-checkbox"]`).check()
 *   page.locator(`[data-ui-id="report-view-card-${id}-select"]`).click()
 *   page.locator('[data-ui-id="report-bulk-actions-delete"]').click()
 */
export const REPORT_UI_ROLES = {
  PAGE: "report-page",
  /** "Report" breadcrumb — navigates back to /reports */
  BREADCRUMB_REPORTS: "report-breadcrumb-reports",
  /** Current report name in the breadcrumb */
  BREADCRUMB_CURRENT: "report-breadcrumb-current",
  SEARCH: "report-views-search",
  BUILD_REPORT: "report-build-report",
  VIEWS_LIST: "report-views-list",
  EMPTY: "report-views-empty",
  /** Individual view card – append `-{viewId}` */
  VIEW_CARD: "report-view-card",
  BULK_ACTIONS: "report-bulk-actions",
  BULK_ACTIONS_COUNT: "report-bulk-actions-count",
  BULK_ACTIONS_TRIGGER: "report-bulk-actions-trigger",
  BULK_ACTIONS_EDIT_DATE_PERIOD: "report-bulk-actions-edit-date-period",
  BULK_ACTIONS_DELETE: "report-bulk-actions-delete",
  BULK_ACTIONS_CLOSE: "report-bulk-actions-close",
  VIEW_DETAIL_MODAL: "view-detail-modal",
} as const;

/**
 * Suffixes inside the V2 view detail panel: `view-detail-modal-{role}`.
 *
 * Sections are `view-detail-modal-section-{sectionId}` where sectionId is
 * `question`, `measuring`, `not-available`, or the panel model's own section id
 * (`comparing`, `for`, `filters`, `models`).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="view-detail-modal-section-question"]')
 *   page.locator('[data-ui-id="view-detail-modal-question-text"]')
 *   page.locator('[data-ui-id^="view-detail-modal-question-response-"]')
 *   page.locator('[data-ui-id="view-detail-modal-measuring-metric"]')
 *   page.locator('[data-ui-id="view-detail-modal-section-for-toggle"]').click()
 *   page.locator('[data-ui-id="view-detail-modal-select"]').click()
 */
export const VIEW_DETAIL_UI_ROLES = {
  NAME: "name",
  CLOSE: "close",
  SUMMARY: "summary",
  /** Skeleton shown while the view detail loads */
  LOADING: "loading",
  /** Root of the loaded panel content */
  LOADED: "loaded",
  ERROR: "error",
  /** Append `-{sectionId}` */
  SECTION: "section",
  QUESTION: "question",
  QUESTION_TEXT: "question-text",
  /** Append `-{response}` */
  QUESTION_RESPONSE: "question-response",
  /** Section id, and `-{key}` prefix for each measuring item */
  MEASURING: "measuring",
  NOT_AVAILABLE: "not-available",
  /** "Show all N" / "Show fewer" — appended to a section id */
  TOGGLE: "toggle",
  DELETE: "delete",
  SELECT: "select",
} as const;

/** Suffixes inside a view card: `report-view-card-{viewId}-{role}` */
export const VIEW_CARD_UI_ROLES = {
  CHECKBOX: "checkbox",
  /** "{module} / {tab}" header */
  MODULE: "module",
  MODULE_ICON: "module-icon",
  NAME: "name",
  PERIOD: "period",
  DETAILS: "details",
  MENU_TRIGGER: "menu-trigger",
  MENU_EDIT: "menu-edit",
  /** V2 only */
  MENU_MAKE_DEFAULT: "menu-make-default",
  /** Legacy only */
  MENU_CLONE: "menu-clone",
  MENU_EXPORT: "menu-export",
  MENU_EXPORT_PPT: "menu-export-ppt",
  MENU_EXPORT_EXCEL: "menu-export-excel",
  MENU_DELETE: "menu-delete",
  /** "Select view" footer link — opens the view */
  SELECT: "select",
  /** Brand/market entity strip under the card header */
  BRANDS_STRIP: "brands-strip",
  MARKETS_STRIP: "markets-strip",
  /** Legacy footer chips */
  FILTERS_CHIP: "filters-chip",
  SEGMENTS_CHIP: "segments-chip",
  QUESTIONS_CHIP: "questions-chip",
  /** V2 footer constraint chip, its tooltip, and related badges */
  CONSTRAINT_CHIP: "constraint-chip",
  UNAVAILABLE_TOOLTIP: "unavailable-tooltip",
  UNSUPPORTED_WARNING: "unsupported-warning",
  NO_CONSTRAINTS: "no-constraints",
  UNAVAILABLE_BADGE: "unavailable-badge",
} as const;
